/**
 * Firebase Middleware
 * 
 * Handles all Firebase Realtime Database operations for the application.
 * Provides abstraction layer between components and Firebase API.
 * 
 * COLLECTIONS:
 * - user_device_info: Stores device tracking data with session management
 * - contact_enquiries: Stores contact form submissions with WhatsApp tracking
 * 
 * USAGE:
 * ```javascript
 * import { saveUserDeviceInfo, isFirebaseConfigured } from './middleware/firebaseMiddleware';
 * 
 * if (isFirebaseConfigured()) {
 *   const docId = await saveUserDeviceInfo(deviceData);
 * }
 * ```
 * 
 * FUNCTIONS:
 * - saveUserDeviceInfo(deviceData) → Returns document ID (key)
 * - updateUserDeviceSession(docId, sessionEndData) → Updates session end time
 * - saveContactEnquiry(enquiryData) → Saves form submission, returns document ID
 * - updateEnquiryWhatsAppStatus(docId, updatedData) → Tracks WhatsApp link click
 * - updateEnquiryStatus(docId, status, note) → Admin status updates
 * - isFirebaseConfigured() → Checks if Firebase credentials are set
 * 
 * See docs/FIREBASE_SETUP.md for setup instructions.
 */

import { ref, push, update, serverTimestamp } from 'firebase/database';
import database from '../config/firebase';

/**
 * Collection paths in Firebase Realtime Database
 */
const COLLECTIONS = {
  USER_DEVICE_INFO: 'user_device_info',
  CONTACT_ENQUIRIES: 'contact_enquiries'
};

/**
 * Save user device information to Firebase Realtime Database
 * @param {Object} deviceData - Device information from userDeviceInfo.getFirebaseData()
 * @returns {Promise<string>} Document ID (key)
 */
export async function saveUserDeviceInfo(deviceData) {
  try {
    // Add server timestamp
    const dataToSave = {
      ...deviceData,
      savedAt: serverTimestamp()
    };

    const deviceRef = ref(database, COLLECTIONS.USER_DEVICE_INFO);
    const newDeviceRef = push(deviceRef, dataToSave);
    
    return newDeviceRef.key;
  } catch (error) {
    /* eslint-disable no-console */
    console.error('❌ Error saving user device info:', error);
    /* eslint-enable no-console */
    return null;
  }
}

/**
 * Update user device info session end data
 * @param {string} docId - Document ID to update
 * @param {Object} sessionEndData - Session end information
 * @returns {Promise<boolean>} Success status
 */
export async function updateUserDeviceSession(docId, sessionEndData) {
  if (!docId) return false;
  
  try {
    const deviceRef = ref(database, `${COLLECTIONS.USER_DEVICE_INFO}/${docId}`);
    
    await update(deviceRef, {
      'session.endTimestamp': sessionEndData.session.endTimestamp,
      'session.endDate': sessionEndData.session.endDate,
      'session.durationMs': sessionEndData.session.durationMs,
      'session.durationReadable': sessionEndData.session.durationReadable,
      'metadata.updatedAt': sessionEndData.metadata.updatedAt,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    /* eslint-disable no-console */
    console.error('❌ Error updating user device session:', error);
    /* eslint-enable no-console */
    return false;
  }
}

/**
 * Save contact enquiry to Firebase Realtime Database
 * @param {Object} enquiryData - Enquiry data from formatContactEnquiry()
 * @returns {Promise<string>} Document ID (key)
 */
export async function saveContactEnquiry(enquiryData) {
  try {
    // Add server timestamp
    const dataToSave = {
      ...enquiryData,
      savedAt: serverTimestamp()
    };

    const enquiryRef = ref(database, COLLECTIONS.CONTACT_ENQUIRIES);
    const newEnquiryRef = push(enquiryRef, dataToSave);
    
    return newEnquiryRef.key;
  } catch (error) {
    /* eslint-disable no-console */
    console.error('❌ Error saving contact enquiry:', error);
    /* eslint-enable no-console */
    return null;
  }
}

/**
 * Update contact enquiry WhatsApp status
 * @param {string} docId - Document ID to update
 * @param {Object} updatedData - Updated enquiry data
 * @returns {Promise<boolean>} Success status
 */
export async function updateEnquiryWhatsAppStatus(docId, updatedData) {
  if (!docId) return false;
  
  try {
    const enquiryRef = ref(database, `${COLLECTIONS.CONTACT_ENQUIRIES}/${docId}`);
    
    await update(enquiryRef, {
      'communication.whatsappSent': updatedData.communication.whatsappSent,
      'communication.whatsappTimestamp': updatedData.communication.whatsappTimestamp,
      'metadata.updatedAt': updatedData.metadata.updatedAt,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    /* eslint-disable no-console */
    console.error('❌ Error updating enquiry WhatsApp status:', error);
    /* eslint-enable no-console */
    return false;
  }
}

/**
 * Update contact enquiry status
 * @param {string} docId - Document ID to update
 * @param {string} newStatus - New status ("new" | "in-progress" | "contacted" | "closed")
 * @param {string} note - Optional note to add
 * @returns {Promise<boolean>} Success status
 */
export async function updateEnquiryStatus(docId, newStatus, note = null) {
  if (!docId) return false;
  
  try {
    const enquiryRef = ref(database, `${COLLECTIONS.CONTACT_ENQUIRIES}/${docId}`);
    
    const updates = {
      'enquiry.status': newStatus,
      'metadata.updatedAt': new Date().toISOString(),
      updatedAt: serverTimestamp()
    };

    if (newStatus === 'contacted') {
      updates['communication.firstResponse'] = new Date().toISOString();
    }

    if (note) {
      updates['communication.notes'] = note;
    }

    updates['communication.lastContact'] = new Date().toISOString();

    await update(enquiryRef, updates);
    
    return true;
  } catch (error) {
    /* eslint-disable no-console */
    console.error('❌ Error updating enquiry status:', error);
    /* eslint-enable no-console */
    return false;
  }
}

/**
 * Check if Firebase is configured
 * @returns {boolean}
 */
export function isFirebaseConfigured() {
  const isConfigured = database && 
    process.env.REACT_APP_FIREBASE_API_KEY && 
    process.env.REACT_APP_FIREBASE_PROJECT_ID &&
    process.env.REACT_APP_FIREBASE_DATABASE_URL;
  
  return !!isConfigured;
}

export { COLLECTIONS };
export default {
  saveUserDeviceInfo,
  updateUserDeviceSession,
  saveContactEnquiry,
  updateEnquiryWhatsAppStatus,
  updateEnquiryStatus,
  isFirebaseConfigured,
  COLLECTIONS
};
